// Store for managing the list of DomainTerms within a Domain (fetch, create, delete).
import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { ref, watch } from "vue";

import { api, type types } from "@/lib/openapi";
import { useCurrentEntityManager } from "@/stores/entity-store";
import { useReleaseStore } from "@/stores/release-store";
import { type SyncReturn, useSyncStore } from "@/stores/sync-store";

export type DomainTerm = types.DomainTerm;

export const useDomainTermsStore = defineStore("domainTerms", () => {
    const domainTerms = ref<DomainTerm[]>();
    const isLoading = ref(false);
    const toast = useToast();
    const syncStore = useSyncStore();
    const { domain } = useCurrentEntityManager();
    const releaseStore = useReleaseStore();
    const controller = ref(new AbortController());
    const statusCode = ref<number>();
    const _error = ref();

    syncStore.$onAction(({ name, after }) => {
        if (name === "sync") {
            after((fullySyncedIdentities: SyncReturn) => {
                if (
                    fullySyncedIdentities.some(
                        (identity) => identity.entityType === "domain_term"
                    )
                ) {
                    getDomainTerms();
                }
            });
        }
    });

    const getDomainTerms = async () => {
        controller.value.abort();
        controller.value = new AbortController();
        if (!domain.value?.identity) {
            domainTerms.value = undefined;
            return;
        }
        isLoading.value = true;
        try {
            const { response, data } =
                await api.startDomainDomainTermMgtGetDomainTermsStartPost({
                    body: {
                        domain_id: domain.value.identity,
                    },
                    signal: controller.value.signal,
                });
            statusCode.value = response.status;
            if (response.ok) {
                domainTerms.value = data as DomainTerm[];
            } else {
                _error.value = data;
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") return;
            throw e;
        } finally {
            isLoading.value = false;
        }
    };

    watch(
        () => domain.value?.identity,
        async (newId, oldId) => {
            if (newId && newId !== oldId) {
                await getDomainTerms();
            }
        },
        { immediate: true }
    );

    // Re-fetch on revision (thread branch / release tag) change — a ?thread=
    // query change doesn't alter route params, so the domain watcher won't fire.
    watch(
        () => releaseStore.activeRevision,
        async () => {
            await getDomainTerms();
        }
    );

    watch(_error, () => {
        if (_error.value)
            toast.add({
                severity: "error",
                summary: "Error " + statusCode.value,
                detail: _error.value?.detail?.[0]?.msg ?? _error.value,
            });
        _error.value = undefined;
    });

    const createDomainTerm = async (name: string, description: string) => {
        if (!name || !domain.value) return;
        isLoading.value = true;

        const { response, data } =
            await api.startDomainDomainTermMgtCreateDomainTermStartPost({
                body: {
                    domain_id: domain.value.identity,
                    name,
                    description: new File([description], name, {
                        type: "text/markdown",
                    }),
                },
            });
        statusCode.value = response.status;
        if (response.ok) {
            await getDomainTerms();
        } else {
            _error.value = data;
        }
        isLoading.value = false;
    };

    const deleteDomainTerm = async (domainTermId: string) => {
        if (!domainTermId) return;
        isLoading.value = true;

        const { response, data } =
            await api.startDomainDomainTermMgtDeleteDomainTermStartPost({
                body: { domain_term_id: domainTermId },
            });
        statusCode.value = response.status;
        if (response.ok) {
            await getDomainTerms();
        } else {
            _error.value = data;
        }
        isLoading.value = false;
    };

    return {
        domainTerms,
        isLoading,
        statusCode,
        getDomainTerms,
        createDomainTerm,
        deleteDomainTerm,
    };
});

export const useDomainTermsManager = () => storeToRefs(useDomainTermsStore());
